/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, cloneElement } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Paper,
  Switch,
  FormControlLabel,
  Grid,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  DragIndicator,
  Visibility,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  getAllProductsWithoutPagination,
  deleteProduct,
  reorderProducts,
  updateProductActive,
} from "actions";
import { clearError } from "features/productsSlice";
import { ComponentHeader } from "components";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { FILE_TYPE } from "constants/fileTypes";
import "./Products.scss";
import { BrandButton } from "components/brand";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;

const SortableItem = ({ id, children }) => {
  const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="sortable-item" {...attributes}>
      {cloneElement(children, { dragHandleProps: listeners })}
    </div>
  );
};

const ProductCard = ({
  product,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  getImageUrl,
  dragHandleProps,
  isActiveLoading,
}) => {
  return (
    <Card className={`product-card${!product.active ? " inactive" : ""}`}>
      <CardContent className="card-content">
        <Box className="card-header">
          <Box {...dragHandleProps} className="drag-handle">
            <DragIndicator color="primary" />
          </Box>
          <Chip
            label={`${ADMIN_TEXTS.common.order}: ${product.order}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={
              product.active
                ? ADMIN_TEXTS.common.active
                : ADMIN_TEXTS.common.inactive
            }
            size="small"
            color={product.active ? "success" : "default"}
            variant="outlined"
            className="status-chip"
          />
        </Box>

        {product.image && (
          <Box className="image-container">
            {product?.image?.type === FILE_TYPE.VIDEO ? (
              <video
                src={getImageUrl(product.image)}
                className="product-image"
                controls
              />
            ) : (
              <img
                src={getImageUrl(product.image)}
                alt="Product"
                className="product-image"
              />
            )}
          </Box>
        )}

        <Typography variant="h6" component="h3" mb={0.5} noWrap>
          {product.title || ADMIN_TEXTS.products.noData}
        </Typography>
        {product.price != null && (
          <Typography variant="subtitle1" fontWeight="bold" color="primary" mb={1}>
            ${product.price}
          </Typography>
        )}

        <Box
          className="description-content"
          dangerouslySetInnerHTML={{
            __html: product.description || ADMIN_TEXTS.products.noData,
          }}
        />

        <Box className="card-actions">
          <Box className="toggle-container">
            <FormControlLabel
              control={
                <Switch
                  checked={product.active}
                  onChange={() => onToggleActive(product.id, !product.active)}
                  disabled={isActiveLoading}
                  size="small"
                />
              }
              label={ADMIN_TEXTS.common.active}
              className="toggle-active"
            />
          </Box>
          <Box className="action-buttons">
            <IconButton
              size="small"
              onClick={() => onView(product.id)}
              className="view-button"
            >
              <Visibility />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onEdit(product.id)}
              className="edit-button"
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(product.id, product.title)}
              className="delete-button"
            >
              <Delete />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const ProductsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    allProducts: reduxProducts,
    loading,
    reorderLoading,
    isActiveLoading,
    error,
  } = useSelector((state) => state.products);
  const [products, setProducts] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
    title: "",
  });
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    })
  );

  const handleDelete = () => {
    dispatch(deleteProduct(deleteDialog.id));
    setDeleteDialog({ open: false, id: null, title: "" });
  };

  const handleView = (id) => {
    navigate(`/products/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/products/${id}/edit`);
  };

  const handleDeleteClick = (id, title) => {
    setDeleteDialog({
      open: true,
      id,
      title: title || "Untitled Product",
    });
  };

  const handleToggleActive = async (id, active) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, active } : p)),
    );
    try {
      await dispatch(updateProductActive({ id, active })).unwrap();
    } catch {
      setProducts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, active: !active } : p)),
      );
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = products.findIndex((item) => item.id === active.id);
      const newIndex = products.findIndex((item) => item.id === over.id);
      const newSorted = arrayMove(products, oldIndex, newIndex);

      const updatedItems = newSorted.map((item, index) => ({
        ...item,
        order: index + 1,
      }));

      setProducts(updatedItems);

      const productIds = updatedItems.map((item) => item.id);
      dispatch(reorderProducts(productIds));
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    return `${imageBase}${image.path}`;
  };

  useEffect(() => {
    dispatch(getAllProductsWithoutPagination());
  }, []);

  useEffect(() => {
    if (reduxProducts) {
      setProducts([...reduxProducts].sort((a, b) => a.order - b.order));
    }
  }, [reduxProducts]);

  useEffect(() => {
    if (error) {
      if (reduxProducts) {
        setProducts(reduxProducts);
      }
      dispatch(clearError());
    }
  }, [error, reduxProducts]);

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="products-list">
      <Box className="list-header">
        <ComponentHeader title={ADMIN_TEXTS.products.management} />
        <BrandButton
          variant="primary"
          startIcon={<Add />}
          onClick={() => navigate("/products/create")}
        >
          {ADMIN_TEXTS.products.createProduct}
        </BrandButton>
      </Box>

      {products.length === 0 ? (
        <Paper className="empty-state">
          <Typography variant="h6" color="text.secondary" mb={2}>
            {ADMIN_TEXTS.products.noProducts}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {ADMIN_TEXTS.products.noProductsMessage}
          </Typography>
          <BrandButton
            variant="primary"
            startIcon={<Add />}
            onClick={() => navigate("/products/create")}
          >
            {ADMIN_TEXTS.products.createFirstProduct}
          </BrandButton>
        </Paper>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={products.map((item) => item.id)}
            strategy={rectSortingStrategy}
          >
            <Box className="sortable-container">
              <Grid container spacing={2}>
                {products.map((product) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={product.id}
                    className="products-grid-item"
                  >
                    <SortableItem id={product.id}>
                      <ProductCard
                        product={product}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                        onToggleActive={handleToggleActive}
                        getImageUrl={getImageUrl}
                        isActiveLoading={isActiveLoading}
                      />
                    </SortableItem>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </SortableContext>
        </DndContext>
      )}

      {reorderLoading && (
        <Box className="reorder-loading">
          <Alert severity="info" className="loading-alert">
            <CircularProgress size={16} />
            {ADMIN_TEXTS.products.reorderingProducts}
          </Alert>
        </Box>
      )}

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, title: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{ADMIN_TEXTS.products.deleteProduct}</DialogTitle>
        <DialogContent>
          <Typography>
            {ADMIN_TEXTS.products.deleteConfirm} "{deleteDialog.title}"?{" "}
            {ADMIN_TEXTS.products.deleteConfirmMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <BrandButton
            variant="secondary"
            onClick={() =>
              setDeleteDialog({ open: false, id: null, title: "" })
            }
          >
            {ADMIN_TEXTS.common.cancel}
          </BrandButton>
          <BrandButton variant="error" onClick={handleDelete}>
            {ADMIN_TEXTS.common.delete}
          </BrandButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
